<script setup lang="ts">
import type { BoxFragment, CommonContentRecord } from '~/graphql/generated'

type Props = BoxFragment & { commonContent?: CommonContentRecord }

const props = withDefaults(defineProps<Props>(), {
  backgroundColor: '',
  sections: () => [],
  removeVerticalPaddings: false,
  commonContent: undefined,
})

const isTitleSectionHydrated = ref(false)
const isTestimonialShortHydrated = ref(false)
const isImageWithFeaturesHydrated = ref(false)

const bgClass = computed(() => {
  switch (props.backgroundColor) {
    case '#FBBA66': // down
    case '#EAEFF8': // old blue becomes down
      return 'tw-bg-down'
    case '#2B2B2B': // charcoal
    case '#131313': // old black becomes charcoal
      return 'tw-bg-fill-alt'
    case '#EEEEEE': // light grey
      return 'tw-bg-fill-discrete3'
    case '#D3ADFO': // lavender
      return 'tw-bg-fill-accent'
    default: // lin
      return 'tw-bg-fill'
  }
})

function getComponentAndBindFromTypeName(
  section: (typeof props.sections)[number],
) {
  // For performance, use `defineAsyncComponent` to load components only when needed (see https://learnvue.co/articles/lazy-load-components).
  // Before adding to this list, consider if they can be lazily hydrated instead.
  switch (section.__typename) {
    // NOTE: commenting out AudioAnalyzer until such time as when it can finished and ready for prod.
    // case 'AudioAnalyzerRecord':
    //   return markRaw(defineAsyncComponent(() => import('~/components/dato/AudioAnalyzer.vue')))

    // SORTED ALPHABETICALLY
    case 'BreakEvenCalculatorHeroRecord':
      return markRaw(
        defineAsyncComponent(
          () => import('~/components/dato/BreakEvenCalculatorHero.vue'),
        ),
      )
    case 'ChartsHeaderRecord':
      return markRaw(
        defineAsyncComponent(
          () => import('~/components/dato/Charts/Header.vue'),
        ),
      )
    case 'ChartRecord':
      return markRaw(
        defineAsyncComponent(
          () => import('~/components/dato/Charts/Charts.vue'),
        ),
      )
    case 'CtasSectionRecord':
      return markRaw(
        defineAsyncComponent(() => import('~/components/dato/CtasSection.vue')),
      )
    case 'CuratorCardRecord':
      return markRaw(
        defineAsyncComponent(() => import('~/components/dato/CuratorCard.vue')),
      )
    case 'EntityCarouselRecord':
      return markRaw(
        defineAsyncComponent(
          () => import('~/components/dato/EntityCarousel.vue'),
        ),
      )
    case 'EntityStackedLayoutRecord':
      return markRaw(
        defineAsyncComponent(
          () => import('~/components/dato/EntityStackedLayout.vue'),
        ),
      )
    case 'FullWidthImageRecord':
      return markRaw(
        defineAsyncComponent(
          () => import('~/components/dato/FullWidthImage.vue'),
        ),
      )
    case 'FullWidthVideoRecord':
      return markRaw(
        defineAsyncComponent(
          () => import('~/components/dato/FullWidthVideo.vue'),
        ),
      )
    case 'IframeRecord':
      return markRaw(
        defineAsyncComponent(() => import('~/components/dato/Iframe.vue')),
      )
    case 'GrooverPlusHeroRecord':
      return markRaw(
        defineAsyncComponent(
          () => import('~/components/dato/Subscription/Hero.vue'),
        ),
      )
    case 'StreamingCalculatorHeroRecord':
      return markRaw(
        defineAsyncComponent(
          () => import('~/components/dato/StreamingCalculatorHero.vue'),
        ),
      )
    default:
      return 'div'
  }
}
</script>

<template>
  <div
    class="tw-flex tw-flex-col tw-gap-3xl md:tw-gap-5xl lg:tw-gap-14"
    :class="[
      bgClass,
      { 'tw-py-4xl md:tw-py-14 lg:tw-py-7xl': !removeVerticalPaddings },
    ]"
  >
    <div
      v-for="(section, index) in sections.filter((section) => 'id' in section)"
      :key="section.id ?? -index"
      class=""
    >
      <component
        v-bind="{ ...section, commonContent }"
        :is="getComponentAndBindFromTypeName(section)"
      />
      <!-- 
       - Lazily hydrated components -
       Note that they're still rendered in SSR, but not hydrated with js until specified.
       Don't use for js-dependent "above the fold components" or those that require js for styling and consider which hydration strategy to use 
       e.g. `hydrate-on-visible`, `hydrate-never` (for static components), etc.
       -->
      <LazyVisibleDatoAccordion
        v-if="section.__typename === 'AccordionRecord'"
        v-bind="{ ...section }"
        hydrate-on-visible
      />
      <LazyVisibleDatoBento
        v-if="section.__typename === 'BentoRecord'"
        v-bind="{ ...section }"
        hydrate-on-visible
      />
      <LazyVisibleDatoCardCluster
        v-if="section.__typename === 'CardClusterRecord'"
        v-bind="{ ...section }"
        hydrate-on-visible
      />
      <LazyVisibleDatoChartsFooter
        v-if="section.__typename === 'ChartsFooterRecord'"
        v-bind="{ ...section }"
        hydrate-on-visible
      />
      <LazyNeverDatoCompactHero
        v-if="section.__typename === 'CompactHeroRecord'"
        v-bind="{ ...section }"
        hydrate-never
      />
      <LazyVisibleDatoCuratorCarousel
        v-if="section.__typename === 'CuratorCarouselRecord'"
        v-bind="{ ...section }"
        hydrate-on-visible
      />
      <LazyNeverDatoDivider
        v-if="section.__typename === 'DividerRecord'"
        hydrate-never
      />
      <LazyIdleDatoHero
        v-if="section.__typename === 'HeroRecord'"
        v-bind="{ ...section }"
        hydrate-on-idle
      />
      <LazyMediaQueryDatoIconList
        v-if="section.__typename === 'IconListRecord'"
        v-bind="{ ...section }"
        hydrate-on-media-query="(max-width: 767px)"
      />
      <LazyVisibleDatoImageSlideshow
        v-if="section.__typename === 'ImageSlideshowRecord'"
        v-bind="{ ...section }"
        hydrate-on-visible
      />
      <LazyVisibleDatoImageWithFeatures
        v-if="section.__typename === 'ImageWithFeatureRecord'"
        v-bind="{ ...section }"
        :is-hydrated="isImageWithFeaturesHydrated"
        :hydrate-on-visible="{
          threshold: 0.2,
          rootMargin: '0px 0px -50px 0px',
        }"
        @hydrated="isImageWithFeaturesHydrated = true"
      />
      <LazyVisibleDatoFeaturedUser
        v-if="section.__typename === 'FeaturedUserRecord'"
        v-bind="{ ...section }"
        hydrate-on-visible
      />
      <LazyIdleDatoLogoCarousel
        v-if="section.__typename === 'LogoCarouselRecord'"
        v-bind="{ ...section }"
        hydrate-on-idle
      />
      <LazyVisibleDatoTestimonial
        v-if="section.__typename === 'TestimonialRecord'"
        v-bind="{ ...section }"
        hydrate-on-visible
      />
      <LazyVisibleDatoTestimonialCarousel
        v-if="section.__typename === 'TestimonialCarouselRecord'"
        v-bind="{ ...section }"
        hydrate-on-visible
      />
      <LazyVisibleDatoTestimonialShort
        v-if="section.__typename === 'TestimonialShortRecord'"
        v-bind="{ ...section }"
        :is-hydrated="isTestimonialShortHydrated"
        :hydrate-on-visible="{
          threshold: 0.2,
          rootMargin: '0px 0px -50px 0px',
        }"
        @hydrated="isTestimonialShortHydrated = true"
      />
      <LazyVisibleDatoTextImage
        v-if="section.__typename === 'TextImageRecord'"
        v-bind="{ ...section, commonContent }"
        hydrate-on-visible
      />
      <LazyVisibleDatoTitleSection
        v-if="section.__typename === 'TitleSectionRecord'"
        v-bind="{ ...section }"
        :hydrate-on-visible="{
          threshold: 0.2,
          rootMargin: '0px 0px -128px 0px',
        }"
        :is-hydrated="isTitleSectionHydrated"
        @hydrated="isTitleSectionHydrated = true"
      />
      <LazyVisibleDatoSeoFooter
        v-if="section.__typename === 'SeoFooterRecord'"
        v-bind="{ ...section }"
        hydrate-on-visible
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
// "alt" coloring schema
.tw-bg-fill-alt {
  @apply tw-text-inverse;

  /** --- Specific overrides --- */

  // Hero accent title
  :deep(.tw-text-h1 .tw-text-accent) {
    @apply tw-text-inverse-accent;
  }

  // outlined accent buttons
  :deep(button.tw-text-accent.tw-border-accent:not(:hover)),
  :deep(a.tw-text-accent.tw-border-accent:not(:hover)) {
    @apply tw-border-inverse-accent tw-text-inverse-accent;
  }

  // structured text elements
  &:deep(.prose-base)
    :is(
      :where(h1, h2, h3, h4, h5, h6, p, a):not(
          :where([class~='tw-not-prose'], [class~='tw-not-prose'] *)
        )
    ) {
    @apply tw-text-inverse;

    .currency-swapper {
      @apply tw-text-down;

      > span > span {
        @apply tw-text-down;
      }
    }
  }

  &:deep(.featuredUserCard) {
    @apply tw-bg-fill;

    .tw-text-link-inverse {
      @apply tw-text-primary;
    }

    :last-child {
      @apply tw-text-default-color;
    }
  }

  &:deep(.bentoItem.tw-bg-fill-alt) {
    @apply tw-bg-fill tw-text-default-color;
  }

  &:deep(.image-slideshow .slideshow-bordered-container) {
    @apply tw-border-gray-900;
  }

  &:deep(.testimonial-carousel) {
    @apply tw-text-default-color;
  }

  // curator carousel
  &:deep(.selectionPill) {
    @apply tw-text-inverse;

    &.tw-border-none {
      @apply tw-bg-default tw-text-default-color;
    }
  }

  &:deep(.carousel-with-controls) {
    .bordered {
      @apply tw-border-white;
    }
  }

  &:deep(.logoCarousel) {
    h2 {
      @apply tw-text-discrete3;
    }

    .tw-bg-gradient-to-r:first-of-type {
      @apply tw-from-charcoal tw-to-transparent;
    }

    .tw-bg-gradient-to-r:nth-of-type(2) {
      @apply tw-from-transparent tw-to-charcoal;
    }
  }

  &:deep(.iconList) {
    div > div {
      @apply tw-bg-fill tw-text-default-color;
    }

    .tw-text-icon-discrete3 {
      @apply tw-text-icon-discrete;
    }

    @screen md {
      @apply tw-bg-fill;
    }
  }

  &:deep(.cardCluster .cardClusterLink) {
    // not sure why but "tw-text-link-inverse" is not working here
    @apply tw-text-[#ff7061] focus-visible:tw-outline-focus-inverse;
  }

  &:deep(.accordion .faqAccordionCard) {
    @apply tw-bg-fill;
  }
}

/** --- Overrides for other bg colors --- */
// down "yellow"
.tw-bg-down {
  &:deep(.accordion .tw-bg-default) {
    @apply tw-bg-[#FFDAAC];

    .prose-accordion
      :where(ul > li):not(
        :where([class~='tw-not-prose'], [class~='tw-not-prose'] *)
      )::marker {
      @apply tw-text-default-color;
    }
  }

  &:deep(.logoCarousel) {
    h2 {
      @apply tw-text-default-color;
    }

    .tw-bg-gradient-to-r:first-of-type {
      @apply tw-from-down tw-to-transparent;
    }

    .tw-bg-gradient-to-r:nth-of-type(2) {
      @apply tw-from-transparent tw-to-down;
    }
  }

  &:deep(.testimonials-container) {
    @apply tw-text-default-color;

    .testimonial-panel {
      @apply tw-bg-[#FFDAAC];
    }

    .artist-name {
      @apply tw-text-link;
    }

    .caret-link {
      @apply tw-text-default-color;
    }
  }
}

// gray
.tw-bg-fill-discrete3 {
  &:deep(.accordion .tw-bg-default) {
    @apply tw-bg-fill;
  }

  &:deep(.logoCarousel) {
    .tw-bg-gradient-to-r:first-of-type {
      @apply tw-from-discrete3 tw-to-transparent;
    }

    .tw-bg-gradient-to-r:nth-of-type(2) {
      @apply tw-from-transparent tw-to-discrete3;
    }
  }
}

// lavender
.tw-bg-fill-accent {
  &:deep(.accordion .tw-bg-default) {
    @apply tw-bg-fill-accent-discrete2;

    .prose-accordion {
      // adjust ul padding
      :where(ul):not(
          :where([class~='tw-not-prose'], [class~='tw-not-prose'] *)
        ) {
        padding-inline-start: 1.1em;
      }

      // recolor link text
      :where(a):not(
          :where([class~='tw-not-prose'], [class~='tw-not-prose'] *)
        ) {
        @apply tw-text-accent;
      }

      // turn bullets into checkmarks & recolor them
      :where(ul > li):not(
          :where([class~='tw-not-prose'], [class~='tw-not-prose'] *)
        )::marker {
        @apply tw-font-bold tw-text-icon-accent;
        content: '✓';
      }

      // adjust spacing between list items and their text
      :where(ul > li):not(
          :where([class~='tw-not-prose'], [class~='tw-not-prose'] *)
        )
        p {
        @apply tw-ml-sm;
      }
    }
  }
}
</style>
